<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { Call, Task } from '@stuut/api/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';
  import { AudioMiniPlayer } from '@ui/components/audio';
  import { DirectionBadge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/components/collapsible';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';
  import { downloadFile } from '@ui/utils/utils';

  import { stuut } from '@/api';
  import { CallDrawer } from '@/components/calls';
  import CallSentimentIcon from '@/components/calls/CallSentimentIcon.vue';
  import CallStatusBadge from '@/components/calls/CallStatusBadge.vue';
  import CallTranscript from '@/components/calls/CallTranscript.vue';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    emailContainerClass?: HTMLAttributes['class'];
    hideCollapsibleTrigger?: boolean;
    task?: Task;
  }

  const props = defineProps<Props>();
  const audioPlayerRef = ref();
  const callDetails = ref<Call>();
  const isFetchingCall = ref(false);
  const taskCollectionStore = useTaskCollectionStore();
  const showCallDrawer = ref(false);
  const { currentTask } = storeToRefs(taskCollectionStore);
  const isExpanded = ref(true);

  const currAudioTime = ref(0);
  const isDownloading = ref(false);
  const isDownloaded = ref(false);

  const componentTask = computed(() => props.task ?? currentTask.value);
  const call = computed(() => callDetails.value || componentTask.value?.call);

  const peekFindCall = async (id) => {
    if (isFetchingCall.value || callDetails.value?.id) return callDetails.value;

    isFetchingCall.value = true;

    try {
      callDetails.value = await stuut.calls.retrieve(id);

      return callDetails.value;
    } catch (err: any) {
      console.error(err.clientMessage || 'something went wrong');
    } finally {
      isFetchingCall.value = false;
    }
  };

  const openCallDrawer = async () => {
    callDetails.value = await peekFindCall(call.value?.id);
    showCallDrawer.value = true;

    return callDetails.value;
  };

  const downloadRecording = () => {
    if (isDownloading.value || !call.value?.recording_url) return;

    isDownloading.value = true;

    try {
      if (call.value?.recording_url) {
        downloadFile(call.value?.recording_url, '');
      }
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isDownloading.value = false;
    }
  };

  const playTranscript = (transcript) => {
    const durationMs = call.value?.durationMs ?? 0;
    const startMs = transcript.words[0].start * 1000;
    const offsetStartMs = startMs - 100 >= 0 ? startMs - 100 : startMs;
    const progress = offsetStartMs / durationMs;

    audioPlayerRef.value?.wavesurfer?.seekTo(progress);
    audioPlayerRef.value?.wavesurfer?.play();
  };
</script>

<template>
  <Collapsible v-model:open="isExpanded">
    <div class="space-y-4">
      <div v-if="componentTask?.isCallSource" class="flex items-center justify-between gap-x-2.5">
        <CollapsibleTrigger v-if="!hideCollapsibleTrigger">
          <div class="flex items-center cursor-pointer text-muted-foreground gap-x-2.5">
            <i class="far fa-angle-right transition-all" :class="isExpanded && 'fa-rotate-90'" />
            <span>Outreach call</span>
          </div>
        </CollapsibleTrigger>

        <Button
          v-if="call?.id"
          class="font-bold"
          size="inline"
          variant="link"
          @click="openCallDrawer"
        >
          View full details
        </Button>
      </div>

      <CollapsibleContent>
        <div class="flex flex-col rounded p-5 border bg-background space-y-6">
          <div class="flex items-center justify-between text-xs">
            <div class="flex items-center gap-x-2.5">
              <DirectionBadge v-if="call?.direction" :direction="call?.direction" size="xs" />

              <div class="flex items-baseline gap-x-2.5 text-2xs">
                <span v-if="call?.to_number" class="font-bold">
                  {{ formatPhoneNumber(call?.to_number || '') }}
                </span>

                <Timestamp
                  :time="call?.start_time || call?.created_at"
                  :with-conversion-table="true"
                  :show-from-now="true"
                  class="text-2xs"
                  format="L h:mm A"
                  :local="true"
                />
              </div>
            </div>

            <div class="flex items-center gap-x-2.5">
              <CallSentimentIcon
                v-if="call?.sentiment"
                class="text-sm"
                :sentiment="call?.sentiment"
                :emoji="true"
              />

              <CallStatusBadge v-if="call?.status" size="xs" :status="call?.status" />
            </div>
          </div>

          <div v-if="call?.id" class="space-y-6">
            <div class="flex items-center flex-nowrap w-full">
              <AudioMiniPlayer
                v-if="call?.recording_url"
                ref="audioPlayerRef"
                :src="call?.recording_url"
                :size="35"
                class="w-full rounded-tr-none rounded-br-none"
                @timeupdate="(currTime) => (currAudioTime = currTime)"
              />

              <Button
                class="h-[35px] w-[35px] rounded hover:bg-accent bg-secondary text-foreground rounded-tl-none rounded-bl-none"
                :disabled="isDownloading"
                @click="downloadRecording"
              >
                <i
                  class="fas text-xs!"
                  :class="
                    isDownloading
                      ? 'fa-spinner-third animate-spin'
                      : isDownloaded
                        ? 'fa-check text-success'
                        : 'fa-download'
                  "
                />
              </Button>
            </div>

            <CallTranscript
              v-if="call?.id"
              id="transcript"
              :call="call as Call"
              :audio-time="currAudioTime"
              :allow-segment-play="true"
              @jump-to="(time: number) => audioPlayerRef?.seekTo(time)"
              @play-transcript="playTranscript"
            />
          </div>
        </div>
      </CollapsibleContent>

      <CallDrawer v-model:open="showCallDrawer" :call="call" @close="showCallDrawer = false" />
    </div>
  </Collapsible>
</template>

<style></style>
